import { useEffect, useState } from 'react';
import { get } from '../../../services/api';
import { useLang, type Lang } from '../../../i18n';
import type {
  TeamMember,
  OfficeLocation,
  MissionItem,
  CompanyValue,
  CompanyMilestone,
  CompanyStat,
} from '../../../types';

const LANG_MAP: Record<Lang, string> = {
  'uz-Latn': 'uz_lat',
  'uz-Cyrl': 'uz_cyr',
  ru: 'ru',
  en: 'en',
};

export interface AboutData {
  team: TeamMember[];
  offices: OfficeLocation[];
  mission: MissionItem[];
  values: CompanyValue[];
  milestones: CompanyMilestone[];
  stats: CompanyStat[];
}

interface ListResp<T> {
  items?: T[];
  results?: T[];
}

function pickList<T>(resp: ListResp<T> | T[] | null | undefined): T[] {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.items)) return resp.items;
  if (Array.isArray(resp.results)) return resp.results;
  return [];
}

export function useAboutData() {
  const { lang } = useLang();
  const apiLang = LANG_MAP[lang];
  const [data, setData] = useState<AboutData>({
    team: [],
    offices: [],
    mission: [],
    values: [],
    milestones: [],
    stats: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(false);
    const q = `?lang=${apiLang}`;
    Promise.allSettled([
      get<ListResp<TeamMember>>(`/about/team/${q}`),
      get<ListResp<OfficeLocation>>(`/about/offices/${q}`),
      get<ListResp<MissionItem>>(`/about/mission/${q}`),
      get<ListResp<CompanyValue>>(`/about/values/${q}`),
      get<ListResp<CompanyMilestone>>(`/about/milestones/${q}`),
      get<ListResp<CompanyStat>>(`/about/stats/${q}`),
    ]).then((results) => {
      if (cancel) return;
      const [team, offices, mission, values, milestones, stats] = results;
      const anyOk = results.some((r) => r.status === 'fulfilled');
      setError(!anyOk);
      setData({
        team: team.status === 'fulfilled' ? pickList(team.value) : [],
        offices: offices.status === 'fulfilled' ? pickList(offices.value) : [],
        mission: mission.status === 'fulfilled' ? pickList(mission.value) : [],
        values: values.status === 'fulfilled' ? pickList(values.value) : [],
        milestones: milestones.status === 'fulfilled' ? pickList(milestones.value) : [],
        stats: stats.status === 'fulfilled' ? pickList(stats.value) : [],
      });
      setLoading(false);
    });
    return () => {
      cancel = true;
    };
  }, [apiLang]);

  return { data, loading, error };
}
